import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Building2, CheckCircle2, CalendarDays, MessageSquare, ArrowUpRight, Flag } from "lucide-react";

// Quick actions execute against the TASK's own tenant, never the active one.
const forTenant = (tid) => ({ headers: { "X-Tenant-Id": tid, "X-Entity-Id": "" } });

const STATUSES = [["todo", "To do"], ["in_progress", "In progress"], ["in_review", "In review"], ["done", "Done"]];
const PRIO_RANK = { urgent: 0, high: 1, medium: 2, normal: 2, low: 3 };
const PRIO_COLOR = { urgent: "text-red-600", high: "text-orange-600", medium: "text-slate-600", normal: "text-slate-600", low: "text-slate-400" };

function TaskDrawer({ task, onClose, onChanged }) {
  const tid = task.tenant.id;
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/work/items/${task.id}`, forTenant(tid)).then((r) => setDetail(r.data)).catch(() => setDetail(task)), [task.id, tid]);
  useEffect(() => { load(); }, [load]);
  const d = detail || task;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const patch = (body, msg) => act(() => api.patch(`/work/items/${task.id}`, body, forTenant(tid)), msg);
  const addComment = () => { if (!comment.trim()) return; act(() => api.post(`/work/items/${task.id}/comments`, { body: comment.trim(), internal: true }, forTenant(tid)), "Comment added").then(() => setComment("")); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="allwork-drawer">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} data-testid="allwork-drawer-backdrop" />
      <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="px-5 py-4 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--line)" }}>
          <div className="min-w-0">
            <span className="chip border-indigo-200 text-indigo-700 bg-indigo-50 inline-flex items-center gap-1 mb-2" data-testid="allwork-drawer-company"><Building2 className="w-3 h-3" /> {task.tenant.name}</span>
            <h3 className="font-heading font-semibold text-slate-900 leading-snug">{d.title}</h3>
            {d.display_id && <p className="text-xs text-slate-400 mt-0.5">{d.display_id}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="allwork-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {d.description && <p className="text-sm text-slate-600 whitespace-pre-wrap">{d.description}</p>}

          <div className="space-y-3">
            <p className="eyebrow">Quick actions</p>
            <button onClick={() => patch({ status: "done" }, "Marked complete")} disabled={busy || d.status === "done"} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-slate-700 hover:bg-emerald-50 disabled:opacity-40" style={{ borderColor: "var(--line)" }} data-testid="allwork-complete">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Mark complete
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-20">Status</span>
              <select value={d.status} onChange={(e) => patch({ status: e.target.value }, "Status updated")} disabled={busy} className="flex-1 text-sm border rounded-lg px-2 py-1.5" style={{ borderColor: "var(--line)" }} data-testid="allwork-status">
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-20 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Due</span>
              <input type="date" value={d.due_date || ""} onChange={(e) => patch({ due_date: e.target.value || null }, "Due date updated")} disabled={busy} className="flex-1 text-sm border rounded-lg px-2 py-1.5" style={{ borderColor: "var(--line)" }} data-testid="allwork-due" />
            </div>
          </div>

          <div className="space-y-2">
            <p className="eyebrow flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Comments</p>
            <div className="space-y-2">
              {(d.comments || []).map((c) => (
                <div key={c.id} className="text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid="allwork-comment">
                  <span className="font-medium text-slate-800">{c.author_name}</span>
                  <p className="text-slate-600">{c.body}</p>
                </div>))}
              {(d.comments || []).length === 0 && <p className="text-xs text-slate-400">No comments yet.</p>}
            </div>
            <div className="flex gap-2">
              <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add a comment" className="flex-1 text-sm border rounded-lg px-3 py-1.5" style={{ borderColor: "var(--line)" }} data-testid="allwork-comment-input" />
              <button onClick={addComment} disabled={busy || !comment.trim()} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-40" data-testid="allwork-comment-send">Send</button>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t" style={{ borderColor: "var(--line)" }}>
          <a href={`/t/${task.tenant.slug}/work/items/${task.id}`} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium text-slate-700 hover:bg-slate-50" style={{ borderColor: "var(--line)" }} data-testid="allwork-open-in">
            <ArrowUpRight className="w-4 h-4" /> Open in {task.tenant.name}
          </a>
        </div>
      </div>
    </div>);
}

export default function AllMyWork({ data, reload }) {
  const [sort, setSort] = useState("due");
  const [open, setOpen] = useState(null);
  const tasks = (data?.tasks || []).slice().sort((a, b) =>
    sort === "priority"
      ? (PRIO_RANK[a.priority] ?? 2) - (PRIO_RANK[b.priority] ?? 2)
      : (a.due_date || "9999").localeCompare(b.due_date || "9999"));

  const groups = {};
  for (const t of tasks) { (groups[t.tenant.id] = groups[t.tenant.id] || { tenant: t.tenant, items: [] }).items.push(t); }

  return (
    <div data-testid="allwork-view">
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Sort by</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="border rounded-lg px-2 py-1.5" style={{ borderColor: "var(--line)" }} data-testid="allwork-sort">
            <option value="due">Due date</option>
            <option value="priority">Priority</option>
          </select>
        </div>
      </div>

      {tasks.length === 0 && <div className="panel p-10 text-center text-slate-400 text-sm">Nothing assigned to you across your companies right now.</div>}

      <div className="space-y-5">
        {Object.values(groups).map((g) => (
          <div key={g.tenant.id} className="panel overflow-hidden" data-testid={`allwork-group-${g.tenant.id}`}>
            <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ borderColor: "var(--line)" }}>
              <Building2 className="w-4 h-4 text-indigo-500" />
              <span className="text-sm font-semibold text-slate-900">{g.tenant.name}</span>
              <span className="text-xs text-slate-400 tabular">{g.items.length}</span>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--line)" }}>
              {g.items.map((t) => (
                <button key={t.id} onClick={() => setOpen(t)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50" data-testid={`allwork-row-${t.id}`}>
                  <Flag className={`w-3.5 h-3.5 flex-shrink-0 ${PRIO_COLOR[t.priority] || "text-slate-400"}`} />
                  <span className="flex-1 text-sm text-slate-800 truncate">{t.title}</span>
                  {t.due_date && <span className={`text-xs tabular ${t.due_date < new Date().toISOString().slice(0, 10) ? "text-red-600" : "text-slate-400"}`}>{t.due_date}</span>}
                </button>))}
            </div>
          </div>))}
      </div>

      {open && <TaskDrawer task={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </div>);
}
